import React, { useEffect } from "react";
import { useState } from "react";
import {
  PreferenceProperty,
  PreferenceValue,
  EPreferencePropertyType,
} from "../../../Model/Preference";
import {
  PreferenceItemTextBox,
  PreferenceItemToggle,
  PreferenceItemSelection,
} from "./PreferenceInput";
import "./preference.css";
import MtiButton from "../../Buttons/MtiButton";

type Props = {
  style?: React.CSSProperties;
  isStandAlone?: boolean;
  preferenceProperty: PreferenceProperty;
  propertyPath: string;
  preferenceValues?: PreferenceValue[];
  onChangeValues?: (
    key: string,
    values: string[],
    isMultiValues: boolean
  ) => void;
};

const getValue = (
  preferenceValues: PreferenceValue[] = [],
  preferenceProperty: PreferenceProperty,
  propertyPath: string
) => {
  const propsPath = preferenceProperty.isMultiValue
    ? `${propertyPath}.0`
    : propertyPath;
  const values = preferenceValues?.filter(
    (prefVal) => prefVal.preferenceKey === propsPath
  );
  const value = values.length > 0 ? values[0].preferenceValue : undefined;

  switch (preferenceProperty.propertyType) {
    case EPreferencePropertyType.Boolean:
      return value !== undefined ? (value === "True" ? true : false) : false;
    case EPreferencePropertyType.Double:
    case EPreferencePropertyType.Float:
      return value !== undefined ? parseFloat(value) : 0.0;
    case EPreferencePropertyType.Integer:
    case EPreferencePropertyType.UnsignedInteger:
    case EPreferencePropertyType.Short:
    case EPreferencePropertyType.UnsignedShort:
      return value !== undefined ? parseInt(value) : 0;
    case EPreferencePropertyType.Enum:
      let enumItems = preferenceProperty.enumItems.filter(
        (item) => item.name === value
      );
      return enumItems.length > 0
        ? enumItems[0].name
        : preferenceProperty.enumItems[0].name;
    case EPreferencePropertyType.String:
    case EPreferencePropertyType.Unknown:
      return value !== undefined ? value : "";
    default:
      return "";
  }
};

const getMultiValue = (
  preferenceValues: PreferenceValue[] = [],
  preferenceProperty: PreferenceProperty,
  propertyPath: string
) => {
  let multiValue = [];
  const values = preferenceValues?.filter((prefVal) =>
    prefVal.preferenceKey.includes(propertyPath)
  );

  for (let i = 1; i < values.length; i++) {
    const prefValue = preferenceValues?.filter(
      (prefVal) => prefVal.preferenceKey === `${propertyPath}.${i}`
    );
    const value =
      prefValue.length > 0 ? prefValue[0].preferenceValue : undefined;
    switch (preferenceProperty.propertyType) {
      case EPreferencePropertyType.Boolean:
        multiValue.push(
          value !== undefined ? (value === "True" ? true : false) : false
        );
        break;
      case EPreferencePropertyType.Double:
      case EPreferencePropertyType.Float:
        multiValue.push(value !== undefined ? parseFloat(value) : 0.0);
        break;
      case EPreferencePropertyType.Integer:
      case EPreferencePropertyType.UnsignedInteger:
      case EPreferencePropertyType.Short:
      case EPreferencePropertyType.UnsignedShort:
        multiValue.push(value !== undefined ? parseInt(value) : 0);
        break;
      case EPreferencePropertyType.Enum:
        let enumItems = preferenceProperty.enumItems.filter(
          (item) => item.name === value
        );
        multiValue.push(
          enumItems.length > 0
            ? enumItems[0].name
            : preferenceProperty.enumItems[0].name
        );
        break;
      case EPreferencePropertyType.String:
      case EPreferencePropertyType.Unknown:
        multiValue.push(value !== undefined ? value : "");
        break;
    }
  }
  return multiValue;
};

const getDefaultValue = (preferenceProperty: PreferenceProperty) => {
  switch (preferenceProperty.propertyType) {
    case EPreferencePropertyType.Boolean:
      return false;
    case EPreferencePropertyType.Double:
    case EPreferencePropertyType.Float:
      return 0.0;
    case EPreferencePropertyType.Integer:
    case EPreferencePropertyType.UnsignedInteger:
    case EPreferencePropertyType.Short:
    case EPreferencePropertyType.UnsignedShort:
      return 0;
    case EPreferencePropertyType.Enum:
      return preferenceProperty.enumItems[0].name;
    case EPreferencePropertyType.String:
    case EPreferencePropertyType.Unknown:
      return "";
    default:
      return "";
  }
};

export default function PreferenceItem({
  preferenceProperty,
  style,
  preferenceValues,
  propertyPath,
  isStandAlone = false,
  onChangeValues = () => {},
}: Props) {
  const propertyPathThis = `${propertyPath}.${preferenceProperty.propertyName}`;
  const [value, setValue] = useState(
    getValue(preferenceValues, preferenceProperty, propertyPathThis)
  );
  const [multiValues, setMultiValues] = useState(
    getMultiValue(preferenceValues, preferenceProperty, propertyPathThis)
  );

  const onChange = (newValue: string | number | boolean) => {
    switch (typeof newValue) {
      case "boolean":
        onChangeValues(propertyPathThis, [newValue ? "True" : "False"], false);
        break;
      case "number":
        onChangeValues(propertyPathThis, [newValue.toString()], false);
        break;
      case "string":
        onChangeValues(propertyPathThis, [newValue], false);
    }
  };

  const onChangeMultiValue = (
    index: number,
    changeValue: string | number | boolean
  ) => {
    let newMultiValues = [value, ...multiValues];
    newMultiValues[index] = changeValue;
    let stringNewValues = newMultiValues.map((value) => {
      switch (typeof value) {
        case "boolean":
          return value ? "True" : "False";
        case "number":
          return value.toString();
        case "string":
          return value;
      }
    });
    onChangeValues(propertyPathThis, [...stringNewValues], true);
  };

  const onRemoveMultiValue = (index: number) => {
    let newMultiValues = [value, ...multiValues];
    newMultiValues.splice(index, 1);
    let stringNewValues = newMultiValues.map((value) => {
      switch (typeof value) {
        case "boolean":
          return value ? "True" : "False";
        case "number":
          return value.toString();
        case "string":
          return value;
      }
    });

    onChangeValues(propertyPathThis, stringNewValues, true);
  };

  const onAddMultiValue = () => {
    const newValue = getDefaultValue(preferenceProperty);
    const newMultiValues = [value, ...multiValues, newValue];
    let stringNewValues = newMultiValues.map((value) => {
      switch (typeof value) {
        case "boolean":
          return value ? "True" : "False";
        case "number":
          return value.toString();
        case "string":
          return value;
      }
    });

    onChangeValues(propertyPathThis, stringNewValues, true);
  };

  const getValueElement = (
    pathSuffix: string = "",
    multiValIndex: number = -1
  ) => {
    let valElement = undefined;
    const propsPath = propertyPathThis + pathSuffix;
    const isMultiValue = pathSuffix !== "" && multiValIndex != -1;
    switch (preferenceProperty.propertyType) {
      case EPreferencePropertyType.Boolean:
        valElement = (
          <PreferenceItemToggle
            disabled={preferenceProperty.isReadOnly}
            onChange={
              isMultiValue
                ? (value) => onChangeMultiValue(multiValIndex, value)
                : onChange
            }
            propertyPath={propsPath}
            isCanDelete={isMultiValue && multiValIndex > 0}
            value={
              isMultiValue && multiValIndex > 0
                ? (multiValues[multiValIndex - 1] as boolean)
                : (value as boolean)
            }
            onClickDelete={() => onRemoveMultiValue(multiValIndex)}
            key={`${propsPath}.${multiValIndex}`}
            isUsingUnit={preferenceProperty.unit !== null}
          />
        );
        break;
      case EPreferencePropertyType.Double:
        valElement = (
          <PreferenceItemTextBox
            disabled={preferenceProperty.isReadOnly}
            onChange={
              isMultiValue
                ? (value) => onChangeMultiValue(multiValIndex, value)
                : onChange
            }
            propertyPath={propsPath}
            variant={"number"}
            isInteger={false}
            isCanDelete={isMultiValue && multiValIndex > 0}
            value={
              isMultiValue && multiValIndex > 0
                ? (multiValues[multiValIndex - 1] as number)
                : (value as number)
            }
            onClickDelete={() => onRemoveMultiValue(multiValIndex)}
            key={`${propsPath}.${multiValIndex}`}
            isUsingUnit={preferenceProperty.unit !== null}
          />
        );
        break;
      case EPreferencePropertyType.Float:
        valElement = (
          <PreferenceItemTextBox
            disabled={preferenceProperty.isReadOnly}
            onChange={
              isMultiValue
                ? (value) => onChangeMultiValue(multiValIndex, value)
                : onChange
            }
            propertyPath={propsPath}
            variant={"number"}
            isInteger={false}
            isCanDelete={isMultiValue && multiValIndex > 0}
            value={
              isMultiValue && multiValIndex > 0
                ? (multiValues[multiValIndex - 1] as number)
                : (value as number)
            }
            onClickDelete={() => onRemoveMultiValue(multiValIndex)}
            key={`${propsPath}.${multiValIndex}`}
            isUsingUnit={preferenceProperty.unit !== null}
          />
        );
        break;
      case EPreferencePropertyType.Integer:
        valElement = (
          <PreferenceItemTextBox
            disabled={preferenceProperty.isReadOnly}
            onChange={
              isMultiValue
                ? (value) => onChangeMultiValue(multiValIndex, value)
                : onChange
            }
            propertyPath={propsPath}
            variant={"number"}
            isInteger={true}
            isCanDelete={isMultiValue && multiValIndex > 0}
            value={
              isMultiValue && multiValIndex > 0
                ? (multiValues[multiValIndex - 1] as number)
                : (value as number)
            }
            onClickDelete={() => onRemoveMultiValue(multiValIndex)}
            key={`${propsPath}.${multiValIndex}`}
            isUsingUnit={preferenceProperty.unit !== null}
          />
        );
        break;
      case EPreferencePropertyType.UnsignedInteger:
        valElement = (
          <PreferenceItemTextBox
            disabled={preferenceProperty.isReadOnly}
            onChange={
              isMultiValue
                ? (value) => onChangeMultiValue(multiValIndex, value)
                : onChange
            }
            propertyPath={propsPath}
            variant={"number"}
            isInteger={true}
            isCanDelete={isMultiValue && multiValIndex > 0}
            value={
              isMultiValue && multiValIndex > 0
                ? (multiValues[multiValIndex - 1] as number)
                : (value as number)
            }
            onClickDelete={() => onRemoveMultiValue(multiValIndex)}
            key={`${propsPath}.${multiValIndex}`}
            isUsingUnit={preferenceProperty.unit !== null}
          />
        );
        break;
      case EPreferencePropertyType.Short:
        valElement = (
          <PreferenceItemTextBox
            disabled={preferenceProperty.isReadOnly}
            onChange={
              isMultiValue && multiValIndex > 0
                ? (value) => onChangeMultiValue(multiValIndex, value)
                : onChange
            }
            propertyPath={propsPath}
            variant={"number"}
            isInteger={true}
            isCanDelete={isMultiValue && multiValIndex > 0}
            value={
              isMultiValue && multiValIndex > 0
                ? (multiValues[multiValIndex - 1] as number)
                : (value as number)
            }
            onClickDelete={() => onRemoveMultiValue(multiValIndex)}
            key={`${propsPath}.${multiValIndex}`}
            isUsingUnit={preferenceProperty.unit !== null}
          />
        );
        break;
      case EPreferencePropertyType.UnsignedShort:
        valElement = (
          <PreferenceItemTextBox
            disabled={preferenceProperty.isReadOnly}
            onChange={
              isMultiValue
                ? (value) => onChangeMultiValue(multiValIndex, value)
                : onChange
            }
            propertyPath={propsPath}
            variant={"number"}
            isInteger={true}
            isCanDelete={isMultiValue && multiValIndex > 0}
            value={
              isMultiValue && multiValIndex > 0
                ? (multiValues[multiValIndex - 1] as number)
                : (value as number)
            }
            onClickDelete={() => onRemoveMultiValue(multiValIndex)}
            key={`${propsPath}.${multiValIndex}`}
            isUsingUnit={preferenceProperty.unit !== null}
          />
        );
        break;
      case EPreferencePropertyType.Enum:
        valElement = (
          <PreferenceItemSelection
            disabled={preferenceProperty.isReadOnly}
            onChange={
              isMultiValue
                ? (value) => onChangeMultiValue(multiValIndex, value)
                : onChange
            }
            propertyPath={propsPath}
            displayValues={preferenceProperty.enumItems}
            isCanDelete={isMultiValue && multiValIndex > 0}
            value={
              isMultiValue && multiValIndex > 0
                ? (multiValues[multiValIndex - 1] as string)
                : (value as string)
            }
            onClickDelete={() => onRemoveMultiValue(multiValIndex)}
            key={`${propsPath}.${multiValIndex}`}
            isUsingUnit={preferenceProperty.unit !== null}
          />
        );
        break;
      case EPreferencePropertyType.String:
      case EPreferencePropertyType.Unknown:
        valElement = (
          <PreferenceItemTextBox
            disabled={preferenceProperty.isReadOnly}
            onChange={
              isMultiValue
                ? (value) => onChangeMultiValue(multiValIndex, value)
                : onChange
            }
            propertyPath={propsPath}
            variant={"text"}
            isCanDelete={isMultiValue && multiValIndex > 0}
            value={
              isMultiValue && multiValIndex > 0
                ? (multiValues[multiValIndex - 1] as string)
                : (value as string)
            }
            onClickDelete={() => onRemoveMultiValue(multiValIndex)}
            key={`${propsPath}.${multiValIndex}`}
            isUsingUnit={preferenceProperty.unit !== null}
          />
        );
        break;
    }

    if (preferenceProperty.unit !== null) {
      return (
        <div className="property-value-unit">
          <div>{valElement}</div>
          <div className="property-unit">{preferenceProperty.unit}</div>
        </div>
      );
    }

    return valElement;
  };

  const getMultiValueElement = () => {
    let multiElements = [] as React.ReactNode[];
    const isMultiValue = preferenceProperty.isMultiValue;
    const values = preferenceValues?.filter((prefVal) =>
      prefVal.preferenceKey.includes(propertyPathThis)
    );
    if (values && isMultiValue) {
      for (let i = 0; i < values.length - 1; i++) {
        multiElements.push(getValueElement(`.${i + 1}`, i + 1));
      }
    }
    return multiElements;
  };

  let valueElement = getValueElement(
    preferenceProperty.isMultiValue ? ".0" : "",
    preferenceProperty.isMultiValue ? 0 : -1
  );
  let multiValueElement = getMultiValueElement();

  useEffect(() => {
    setValue(getValue(preferenceValues, preferenceProperty, propertyPathThis));
    setMultiValues(
      getMultiValue(preferenceValues, preferenceProperty, propertyPathThis)
    );
  }, [preferenceValues, preferenceProperty]);

  return (
    <div className={isStandAlone ? "property-item alone" : "property-item"}>
      <div className="property-info">
        <div>{preferenceProperty.displayName}</div>
        {preferenceProperty.description && (
          <div className="property-description">
            {preferenceProperty.description}
          </div>
        )}
      </div>
      <div className="property-value">
        {valueElement}
        {multiValueElement}
        {preferenceProperty.isMultiValue && (
          <MtiButton
            variant="secondary"
            colorVariant="black"
            onClick={onAddMultiValue}
            className="property-value-add"
          >
            <svg
              width="19"
              height="18"
              viewBox="0 0 19 18"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                fillRule="evenodd"
                clipRule="evenodd"
                d="M17 9C17 13.1421 13.6421 16.5 9.5 16.5C5.35786 16.5 2 13.1421 2 9C2 4.85786 5.35786 1.5 9.5 1.5C13.6421 1.5 17 4.85786 17 9ZM8.75 4.5V8.25H5V9.75L8.75 9.75V13.5H10.25V9.75H14V8.25L10.25 8.25L10.25 4.5L8.75 4.5Z"
                fill="#313131"
              />
            </svg>
            <span>ADD MORE</span>
          </MtiButton>
        )}
      </div>
    </div>
  );
}
